import { useEffect, useMemo, useRef, useState } from "react";
import { RotateCcw, RotateCw } from "lucide-react";

// Vertical accordion for the right-pane activity tracker.
// One group expanded at a time; picks auto-advance to the next pending group.
// Top strip ("monitor") lists every current selection so the operator always
// has the full state in view.
export default function ActivityAccordion({
  config,
  order,
  selected = {},
  onSelect,
  isGroupRelevant = () => true,
  bowlerType = null,
}) {
  // Hide groups that don't apply: PACE/SPIN-only, plus Control unless the
  // delivery is airborne (Flat/Loopy/High).
  const visibleOrder = useMemo(() => {
    return order.filter((name) => {
      if (!config[name]) return false;
      if (!isGroupRelevant(name)) return false;
      if (
        name === "Control" &&
        !["Flat", "Loopy", "High"].includes(selected["Air Movement"])
      ) {
        return false;
      }
      return true;
    });
  }, [order, config, isGroupRelevant, selected]);

  // When Control is hidden, surface a one-line hint in the monitor strip
  // explaining why — otherwise scorers wonder where the chip went.
  const controlHidden = (
    config["Control"] &&
    isGroupRelevant("Control") &&
    !["Flat", "Loopy", "High"].includes(selected["Air Movement"])
  );

  const firstUnselected = (list) => {
    const idx = list.findIndex((n) => !selected[n]);
    return idx === -1 ? Math.max(0, list.length - 1) : idx;
  };

  const [expandedIdx, setExpandedIdx] = useState(() =>
    firstUnselected(visibleOrder),
  );
  // Undo stack: each entry { group, prevValue, newValue } so Redo can
  // re-apply newValue and Undo can restore prevValue exactly.
  const [history, setHistory] = useState([]);
  const [redoStack, setRedoStack] = useState([]);

  // When the ball resets (no selections), jump to the top + clear stacks.
  useEffect(() => {
    const anyPicked = Object.values(selected).some(Boolean);
    if (!anyPicked) {
      setExpandedIdx(0);
      setHistory([]);
      setRedoStack([]);
    }
  }, [selected]);

  // Keep the index in range as visibleOrder shrinks/grows.
  useEffect(() => {
    if (expandedIdx >= visibleOrder.length) {
      setExpandedIdx(Math.max(0, visibleOrder.length - 1));
    }
  }, [visibleOrder.length, expandedIdx]);

  // 2026-07-08 — preserve the expanded group by NAME across visibleOrder
  // mutations. When the operator picks Beamer / Full Toss in the pitch
  // map, `Bounce` (and the spin-only groups) drop out of visibleOrder.
  // Without this effect the same numeric expandedIdx suddenly points to
  // the NEXT group in the flow (Delivery Type → Stroke), silently
  // bumping the operator past Delivery Type. We snapshot the previous
  // visibleOrder + look up where the expanded group's NAME lives in the
  // new list, then re-align expandedIdx to it.
  const prevVisibleOrderRef = useRef(visibleOrder);
  useEffect(() => {
    const prevOrder = prevVisibleOrderRef.current;
    const prevName = prevOrder[expandedIdx];
    if (prevName) {
      const newIdx = visibleOrder.indexOf(prevName);
      if (newIdx >= 0 && newIdx !== expandedIdx) {
        setExpandedIdx(newIdx);
      }
    }
    prevVisibleOrderRef.current = visibleOrder;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleOrder]);

  // Auto-advance on external set. When a group's value transitions
  // empty → set without a user chip-click (auto-defaults for Keeper +
  // Bounce, voice fills, AI suggestions), the accordion would stay
  // expanded on the now-set group instead of moving to the next
  // unselected step — operator never sees the next group's options
  // until they manually tap. This effect detects the transition and
  // advances forward. User-triggered picks (handlePick) already advance
  // synchronously, so the effect is idempotent for that path.
  // Founder report 2026-05-28: "options for delivery type should be
  // visible automatically [after Keeper + Bounce default]".
  const prevSelectedRef = useRef(selected);
  useEffect(() => {
    const currentName = visibleOrder[expandedIdx];
    if (currentName) {
      const prevValue = prevSelectedRef.current?.[currentName];
      const currValue = selected[currentName];
      if (!prevValue && currValue) advanceFrom(expandedIdx);
    }
    prevSelectedRef.current = selected;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, expandedIdx, visibleOrder]);

  const advanceFrom = (i) => {
    for (let j = i + 1; j < visibleOrder.length; j++) {
      if (!selected[visibleOrder[j]]) {
        setExpandedIdx(j);
        return;
      }
    }
    setExpandedIdx(visibleOrder.length - 1);
  };

  const handlePick = (groupName, value) => {
    const prevValue = selected[groupName] || "";
    setHistory((h) => [...h, { group: groupName, prevValue, newValue: value }]);
    setRedoStack([]); // any fresh pick invalidates the redo branch
    onSelect(groupName, value);
    advanceFrom(visibleOrder.indexOf(groupName));
  };

  const handleUndo = () => {
    if (history.length === 0) return;
    const entry = history[history.length - 1];
    setHistory((h) => h.slice(0, -1));
    setRedoStack((r) => [...r, entry]);
    onSelect(entry.group, entry.prevValue);
    const idx = visibleOrder.indexOf(entry.group);
    if (idx >= 0) setExpandedIdx(idx);
  };

  const handleRedo = () => {
    if (redoStack.length === 0) return;
    const entry = redoStack[redoStack.length - 1];
    setRedoStack((r) => r.slice(0, -1));
    setHistory((h) => [...h, entry]);
    onSelect(entry.group, entry.newValue);
    advanceFrom(visibleOrder.indexOf(entry.group));
  };

  const setRows = visibleOrder.filter((n) => selected[n]);
  const totalSet = setRows.length;

  return (
    <div className="flex flex-col h-full min-h-0 gap-1.5">
      {/* Monitor — always-visible roll-up of every current selection */}
      <div className="shrink-0 px-1.5 py-1 rounded bg-bg-secondary/50 border border-bg-secondary flex items-center flex-wrap gap-1 text-[10px]">
        <span className="uppercase tracking-wider text-text-secondary shrink-0">
          {totalSet} / {visibleOrder.length}
        </span>
        {bowlerType && (
          <span
            className={`uppercase tracking-wider shrink-0 ${
              bowlerType === "PACE" ? "text-amber-400" : "text-emerald-400"
            }`}
          >
            · {bowlerType}
          </span>
        )}
        <div className="ml-auto shrink-0 inline-flex items-center gap-1">
          <button
            type="button"
            onClick={handleUndo}
            disabled={history.length === 0}
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded uppercase tracking-wider text-[10px] border transition-colors ${
              history.length > 0
                ? "border-amber-500/40 text-amber-300 hover:bg-amber-500/15"
                : "border-bg-secondary text-text-secondary/40 cursor-not-allowed"
            }`}
            title={
              history.length > 0
                ? `Undo last pick (${history[history.length - 1].group})`
                : "Nothing to undo"
            }
          >
            <RotateCcw className="w-3 h-3" />
            Undo
          </button>
          <button
            type="button"
            onClick={handleRedo}
            disabled={redoStack.length === 0}
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded uppercase tracking-wider text-[10px] border transition-colors ${
              redoStack.length > 0
                ? "border-blue-400/40 text-blue-300 hover:bg-blue-500/15"
                : "border-bg-secondary text-text-secondary/40 cursor-not-allowed"
            }`}
            title={
              redoStack.length > 0
                ? `Redo last undo (${redoStack[redoStack.length - 1].group})`
                : "Nothing to redo"
            }
          >
            Redo
            <RotateCw className="w-3 h-3" />
          </button>
        </div>

        {controlHidden && (
          <span
            className="text-blue-300/80 italic"
            title="Air-controlled vs uncontrolled is only meaningful for airborne deliveries (Flat / Loopy / High). The Control chip group will appear once Air Movement is set to one of those."
          >
            · Control chip hides until Air Movement = Flat / Loopy / High
          </span>
        )}
        {setRows.length === 0 ? (
          <span className="text-text-secondary/60 italic">no selections yet</span>
        ) : (
          setRows.map((name) => (
            <button
              key={name}
              type="button"
              onClick={() => setExpandedIdx(visibleOrder.indexOf(name))}
              className="px-2 py-1 rounded-md bg-green-700/20 border border-green-700/40 text-text-primary hover:bg-green-700/35 transition-colors touch-manipulation active:scale-[0.97]"
              title={`${name}: ${selected[name]} — click to edit`}
            >
              <span className="opacity-70">{name}:</span>{" "}
              <span className="font-medium">{selected[name]}</span>
            </button>
          ))
        )}
      </div>

      {/* Accordion body */}
      <div className="flex-1 min-h-0 overflow-y-auto pr-1 flex flex-col gap-1">
        {visibleOrder.map((name, idx) => {
          const data = config[name];
          const value = selected[name];
          const active = idx === expandedIdx;

          if (!active) {
            return (
              <button
                key={name}
                type="button"
                onClick={() => setExpandedIdx(idx)}
                className={`flex items-center justify-between gap-2 w-full text-left px-3 py-2.5 min-h-[42px] rounded-md text-xs transition-colors shrink-0 touch-manipulation active:scale-[0.98] ${
                  value
                    ? "bg-green-700/15 border border-green-700/40 text-text-primary hover:bg-green-700/25"
                    : "bg-bg-secondary/40 border border-border-default text-text-secondary hover:bg-bg-secondary/70 hover:text-text-primary"
                }`}
                title={value ? `${name}: ${value} — tap to change` : `${name} — tap to choose`}
              >
                <span className="font-medium uppercase tracking-wider text-[10px]">
                  {idx + 1}. {name}
                </span>
                <span className="text-[11px] opacity-90 truncate">
                  {value ? `${value} ✓` : "—"}
                </span>
              </button>
            );
          }

          return (
            <div
              key={name}
              className="rounded border border-button-primary/60 bg-bg-secondary/30 p-2 flex flex-col gap-1.5 shrink-0"
            >
              <div className="flex items-center justify-between">
                <span className="text-[11px] uppercase tracking-wider font-semibold text-text-primary">
                  {idx + 1}. {name}
                </span>
                <span className="text-[9px] uppercase tracking-wider text-text-secondary">
                  Step {idx + 1} of {visibleOrder.length}
                </span>
              </div>
              <ChipArea
                data={data}
                selected={value}
                onPick={(item) => handlePick(name, item)}
                bowlerType={bowlerType}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Chip button hoisted to module scope so React does NOT remount every
// chip on every parent render. Previously defined inside ChipArea
// (`const Btn = ...`), which made React treat each render's Btn as a
// brand-new component type — unmount/remount on every keystroke or
// state tick, which on slower touch devices intermittently dropped
// the touchend event mid-tap. Operators were clicking and the chip
// "wouldn't respond." Module-scope component = stable identity =
// React reuses the DOM node + tap target stays bound to its handler.
//
// `active-tap-anim` (defined in src/index.css) gives a brief flash on
// tap so the operator gets visual feedback even before the parent
// state update propagates back as the green "active" colour.
function ChipBtn({ active, label, onPick }) {
  return (
    <button
      type="button"
      onClick={() => onPick(label)}
      // py-2 + min-h-[40px] hits ~Apple HIG 44px once you add the
      // border. text-left wraps long labels gracefully instead of
      // clipping on a single line.
      className={`text-left px-3 py-2 min-h-[40px] rounded-md text-xs font-medium transition-all touch-manipulation border active:scale-[0.97] active:bg-green-600/80 ${
        active
          ? "bg-green-600 text-white shadow-md border-green-500 ring-1 ring-green-400/60"
          : "bg-bg-secondary/60 text-text-primary hover:bg-button-primary/20 hover:border-button-primary/60 border-border-default"
      }`}
      aria-pressed={!!active}
    >
      {active ? <span className="mr-1">✓</span> : null}
      {label}
    </button>
  );
}


function ChipArea({ data, selected, onPick, bowlerType }) {
  const subgroups = data.options ? Object.entries(data.options) : null;
  const items = data.items || null;

  // Delivery Type has Pace/Spinner subgroups — show only the relevant one
  // once the bowler type is known.
  const relevantSubgroups =
    subgroups && bowlerType
      ? subgroups.filter(([key]) =>
          bowlerType === "PACE" ? /pace/i.test(key) : /spin/i.test(key),
        )
      : subgroups;
  const effectiveSubgroups =
    relevantSubgroups && relevantSubgroups.length > 0
      ? relevantSubgroups
      : subgroups;

  // Right pane is the narrow column (~1/3 of viewport). md:grid-cols-5
  // packed 5 chips into ~455px = ~80px wide, which is below the 44px
  // tap-target floor with realistic spacing. Drop to 2 cols on narrow
  // breakpoints, 3 on lg, 4 on xl — gives every chip ~140px of width
  // on a typical 1366-1920 laptop so labels don't truncate either.
  const gridCls = "grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2";

  if (items) {
    return (
      <div className={gridCls}>
        {items.map((it) => (
          <ChipBtn key={it} active={selected === it} label={it} onPick={onPick} />
        ))}
      </div>
    );
  }
  if (effectiveSubgroups && effectiveSubgroups.length === 1) {
    return (
      <div className={gridCls}>
        {effectiveSubgroups[0][1].map((it) => (
          <ChipBtn key={it} active={selected === it} label={it} onPick={onPick} />
        ))}
      </div>
    );
  }
  if (effectiveSubgroups) {
    return (
      <div className="flex flex-col gap-2.5">
        {effectiveSubgroups.map(([groupName, list]) => (
          <div key={groupName}>
            <div className="text-[10px] uppercase tracking-wider text-text-secondary mb-1 px-1">
              {groupName}
            </div>
            <div className={gridCls}>
              {list.map((it) => (
                <ChipBtn key={it} active={selected === it} label={it} onPick={onPick} />
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }
  return null;
}
